import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI, exportAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  DollarSign,
  Calendar,
  User,
  Building2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Filter,
} from 'lucide-react';

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadPayments();
  }, [statusFilter]);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPayments(statusFilter !== 'all' ? statusFilter : null);
      setPayments(res.data);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportAPI.adminPayments();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'platform_payments_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Payments exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'OVERDUE': return <AlertTriangle className="w-4 h-4 text-red-400" />;
      default: return <Clock className="w-4 h-4 text-amber-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'OVERDUE': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'PENDING': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const totalPaid = payments.filter(p => p.status === 'PAID').reduce((s, p) => s + p.amount, 0);
  const totalPending = payments.filter(p => p.status === 'PENDING' || p.status === 'OVERDUE').reduce((s, p) => s + p.amount, 0);

  return (
    <DashboardLayout type="admin" title="Platform Payments">
      <div className="space-y-6" data-testid="admin-payments-page">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl mb-1">
              Platform <span className="text-gold">Payments</span>
            </h2>
            <p className="text-muted-foreground">View all payments across all landlords</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-background border-border" data-testid="payment-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-admin-payments-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-card border-border">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Collected</p>
              <p className="text-2xl font-semibold text-emerald-400">${totalPaid.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Pending</p>
              <p className="text-2xl font-semibold text-amber-400">${totalPending.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Transactions</p>
              <p className="text-2xl font-semibold">{payments.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Payments Table */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-card animate-pulse" />)}
          </div>
        ) : payments.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <DollarSign className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No payments found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {payments.map((p, i) => (
              <Card
                key={p.id}
                className="bg-card border-border hover:border-gold/30 transition-all card-animate"
                style={{ animationDelay: `${i * 0.03}s` }}
                data-testid={`admin-payment-${p.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {getStatusIcon(p.status)}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{p.description}</span>
                          <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(p.status)}`}>
                            {p.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />
                            {p.organisation_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            {p.tenant_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Due: {p.due_date}
                          </span>
                          {p.paid_date && (
                            <span className="flex items-center gap-1 text-emerald-400">
                              <CheckCircle className="w-3.5 h-3.5" />
                              Paid: {p.paid_date}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xl font-semibold whitespace-nowrap">${p.amount.toLocaleString()}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminPayments;
